import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import {
    Disclosure as ReakitDisclosure,
    DisclosureContent as ReakitDisclosureContent,
    useDisclosureState,
} from 'reakit';
import styled from 'styled-components';
import { Box } from '../Box';

type AllOrNone<T> = T | { [K in keyof T]?: never };

interface UncontrolledDisclosureProps {
    children: React.ReactNode;
    /**
     * Trigger that shows/hides the content when interacted with.
     */
    trigger: React.ReactNode;
}

export type DisclosureProps = UncontrolledDisclosureProps &
    AllOrNone<{
        /**
         * Callback fired when the disclosure is attempting to be shown/hidden.
         */
        onToggle?: () => void;
        /**
         * Dictates if the content is shown.
         */
        visible?: boolean;
    }>;

const DisclosureContent = styled(ReakitDisclosureContent)`
    overflow: hidden;
`;

/**
 * Controls visibility of a section of content.
 *
 * Follows the WAI-ARIA Disclosure pattern.
 *
 * Can be controlled using `onToggle` & `visible` together.
 */
export const Disclosure = forwardRef<HTMLDivElement, DisclosureProps>(function (
    { children, onToggle, trigger, visible },
    ref
) {
    const baseDisclosureProps = useDisclosureState({ animated: true, visible });

    // FIXME - closing animation doesn't play if the component is controlled.
    // This should be fixed by better state hooks in Ariakit
    const calculatedDisclosureProps =
        typeof onToggle !== 'undefined'
            ? {
                  ...baseDisclosureProps,
                  toggle: onToggle,
                  visible,
              }
            : baseDisclosureProps;

    const calculatedHeight = calculatedDisclosureProps.visible ? 'auto' : 0;

    return (
        <>
            <ReakitDisclosure {...calculatedDisclosureProps}>
                {(renderProps) => {
                    if (React.isValidElement(trigger)) {
                        return React.cloneElement(trigger, renderProps);
                    }

                    return;
                }}
            </ReakitDisclosure>
            <Box marginTop="0.5bu" />
            <DisclosureContent ref={ref} {...calculatedDisclosureProps}>
                <motion.div
                    animate={{ height: calculatedHeight }}
                    onAnimationComplete={calculatedDisclosureProps.stopAnimation}
                >
                    {children}
                </motion.div>
            </DisclosureContent>
        </>
    );
});
